/*============================================================
   REACT DEPENDENCIES
============================================================*/
import { useState, useEffect } from "react";

/*============================================================
   REDUX DEPENDENCIES
============================================================*/
import { useDispatch, useSelector } from "react-redux";

import { FormControl } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import GridSectionHeader from "../../../widgets/GridSectionHeader";
import GridCheckbox from "../../../widgets/GridCheckbox";

import { valudateTextfield2 } from "../../../../utils/fieldValidator.js";
import { MAX_LENGTH_TEXTAREA } from "../../../../utils/constants.js";

//DATA SLICE - REDUCER
import { updateCKACKR } from "../../../../states/slcCkaCkr.js";

var isExperiencedInvestorError = "";
var investedProductsError = "";
var investmentInstituteError = "";

//TRUE - WHEN COMPONENT IS VALID
var validCKAInvestment = false;

export function getCKAInvestmentValidity() {
  return validCKAInvestment;
}

export default function SectionCKAInvestment({ setEnableNext }) {
  const ckackr = useSelector((state) => state.ckackr);

  const [isExperiencedInvestor, setIsExperiencedInvestor] = useState(ckackr.isExperiencedInvestor);
  const [investedProducts, setInvestedProducts] = useState(Array(2).fill(""));
  const [investmentInstitute, setInvestmentInstitute] = useState(ckackr.investmentInstitute);

  const dispatch = useDispatch();

  useEffect(() => {
    const data = {
      isExperiencedInvestor: isExperiencedInvestor,
      investedProducts: investedProducts,
      investmentInstitute: investmentInstitute,
    };
    updateRedux("investment", data, updateCKACKR);
  }, [isExperiencedInvestor, investedProducts, investmentInstitute]);

  function updateRedux(fieldName, fieldValue, reducer) {
    const action = {
      type: fieldName,
      payload: fieldValue,
    };
    dispatch(reducer(action));
  }

  function handleChange(e, id) {
    let input_data = {
      event: e,
      fieldName: "",
      fieldVariable: "",
      isMandatory: false,
    };

    input_data.e = e;

    let type = "";
    let name = "";

    id = e.target.id.toLowerCase();

    //RADIOBOX
    if (id === "") {
      type = e.target.type;
      name = e.target.name;
      validateRadiobox(e, type, name);
    }

    //CHECKBOX
    validateCheckbox(e, id);

    //TEXTFIELDS
    validateTextfield(id, input_data);

    calculateValidity();
  }

  function validateRadiobox(e, type, name) {
    if (type === "radio" && name === "isExperiencedInvestor") {
      setIsExperiencedInvestor(e.target.value);
      isExperiencedInvestorError = "";
    }
  }

  function validateCheckbox(e, id) {
    const newArr = [...investedProducts];
    const selectedProduct = e.target.value.toUpperCase();
    let index = 0;

    if (id !== "") {
      switch (selectedProduct) {
        case "UNLISTED":
          index = 0;
          break;

        case "LISTED":
          index = 1;
          break;

        default:
          index = -1;
      }

      newArr[index] = newArr[index] === "" ? selectedProduct : "";
      setInvestedProducts(newArr);

      //Check & indicate an education is already selected
      let isSelected = false;
      for (const investment of newArr) {
        if (investment.length !== 0) {
          isSelected = true;
          break;
        }
      }
      if (isSelected) investedProductsError = "";
      else investedProductsError = "Investment product must be selected";
    }
  }

  function validateTextfield(id, input_data) {
    if (id === "investmentinstitute") {
      input_data.fieldName = "Investment institute description";
      input_data.fieldVariable = investmentInstitute;
      input_data.fieldLength = MAX_LENGTH_TEXTAREA;
      input_data.isMandatory = true;
      const result = valudateTextfield2(input_data);

      if (result.errorMessage === "") {
        setInvestmentInstitute(result.value);
        investmentInstituteError = "";
        //updateRedux("networthImpactDesc", result.value, updateSummary);
      } else investmentInstituteError = "Investment institute description is required";
    }
  }

  function calculateValidity() {
    validCKAInvestment = false;

    if (
      isExperiencedInvestorError.length !== 0 ||
      investedProductsError.length !== 0 ||
      investmentInstituteError.length !== 0
    ) {
      validCKAInvestment = false;
      setEnableNext(false);
      return;
    }

    validCKAInvestment = true;
    setEnableNext(true);
  }

  return (
    <>
      <Grid container spacing={2}>
        <GridSectionHeader
          sectionName="Investment Experience"
          sx={{
            p: 1,
            pl: 1.8,
            mt: 0,
            mb: 1,
            fontSize: 18,
            fontWeight: "bold",
            color: "#FFFFFF", //WHITE
            // color: "#000000", //BLACK
            // backgroundColor: "#d9d9d9",
            backgroundColor: "#10B981",
          }}
        />

        <Typography variant="body1">
          3. Have you made at least 6 transactions in the following "Specified Investment Products" in the past 3 years?
        </Typography>
      </Grid>

      <RadioGroup
        row
        name="isExperiencedInvestor"
        defaultValue=""
        value={isExperiencedInvestor}
        onChange={handleChange}
      >
        <FormControl>
          <Stack direction="row">
            <FormControlLabel id="isExperiencedInvestorYes" control={<Radio />} label="Yes" value="Yes" />

            <FormControlLabel id="isExperiencedInvestorNo" control={<Radio />} label="No" value="No" />
          </Stack>
        </FormControl>
      </RadioGroup>

      {/*==============================
        INVESTMENT PRODUCT TYPE
      ==============================*/}
      <Typography variant="h6" gutterBottom sx={{ p: 2, my: 1, border: "1px solid", borderRadius: 5 }}>
        Type of Specified Investment Products:
        <GridCheckbox
          id="unlistedInvestmentProducts"
          columnSize="12"
          label="Unlisted (E.g Unit Trusts, Investment-Linked Policies)"
          value="UNLISTED"
          disabled={isExperiencedInvestor === "YES" ? false : true}
          onChange={handleChange}
        />
        <GridCheckbox
          id="listedInvestmentProducts"
          columnSize="12"
          label="Listed (E.g Exchange Traded Funds, Daily Leverage Certificates, Structured Warrants, Exchange Traded Notes, Futures, Callable Bull/Bear contracts, Options"
          //label="Listed (E.g ETFs, ETNs, Daily Leverage Certificates, Structured Warrants, Futures, Callable contracts, Options"
          value="LISTED"
          disabled={isExperiencedInvestor === "YES" ? false : true}
          onChange={handleChange}
        />
        {investedProductsError && (
          <FormHelperText error={investedProductsError}>{investedProductsError}</FormHelperText>
        )}
      </Typography>

      {/*==============================
        INVESTMENT INSTITUTE DETAILS
      ==============================*/}
      {isExperiencedInvestor === "Yes" && (
        <Grid size={12}>
          <Typography variant="body1">
            Please specify fullname of the Financial Institution(s) where the transactions were carried out.
          </Typography>

          <TextareaAutosize
            id="investmentInstitute"
            placeholder="Type here..."
            style={{ width: "100%" }}
            minRows={5}
            onChange={handleChange}
            defaultValue={investmentInstitute}
          />
          {investmentInstituteError && (
            <FormHelperText error={investmentInstituteError}>{investmentInstituteError}</FormHelperText>
          )}
        </Grid>
      )}
    </>
  );
}
